import React from 'react';
import { Icon } from '@iconify/react';
import { formatDateDDMMYYYY } from '@/utils/dateUtils';
import { resolveFourStats } from '@/components/client/mobile/RaporEventCard';

export interface KartuPemainCardProps {
  playerName: string;
  playerAvatarUrl?: string;
  eventName: string;
  eventDateFormatted: string;
  location?: string;
  sportCategory?: string;
  deltaMMR?: number | null;
  matches: number;
  goals: number;
  assists: number;
  winRate: number;
  point?: number;
  pointLoss?: number;
  isRacket?: boolean;
  selectedStatKeys?: string[];
  rawStats?: any;
  bgImage?: string;
  transparent?: boolean;
  className?: string;
}

const DEFAULT_AVATAR = 'https://cdn.sporto.id/default-avatar.png';
const SPORTO_LOGO_URL = 'https://cdn.sporto.id/sporto.svg';
const DEFAULT_BG_IMAGE = 'https://cdn.sporto.id/bgshare.webp';

export const KartuPemainCard: React.FC<KartuPemainCardProps> = ({
  playerName,
  playerAvatarUrl,
  eventName,
  eventDateFormatted,
  location,
  sportCategory = 'FUTSAL',
  deltaMMR,
  matches,
  goals,
  assists,
  winRate,
  point,
  pointLoss,
  isRacket = false,
  selectedStatKeys,
  rawStats,
  bgImage,
  transparent = true,
  className = '',
}) => {
  const formattedDate = formatDateDDMMYYYY(eventDateFormatted);
  const dateLoc = `${formattedDate}${location ? ` · ${location}` : ''}`;
  const avatarSrc = playerAvatarUrl || DEFAULT_AVATAR;
  const activeBgImage = bgImage || DEFAULT_BG_IMAGE;

  const statItems = resolveFourStats(
    selectedStatKeys,
    rawStats || { goals, assists, matches, win_rate: winRate, point, point_loss: pointLoss },
    isRacket
  );

  const mmrVal = deltaMMR ?? 0;
  const mmrRounded = Math.round(mmrVal);
  const isMMRPositive = mmrRounded > 0;
  const isMMRNegative = mmrRounded < 0;

  const mmrBg = isMMRPositive
    ? 'bg-[#0BCF46]'
    : isMMRNegative
    ? 'bg-[#E11D48]'
    : 'bg-[#0E1726]/60 border border-white/20 backdrop-blur-sm';

  const mmrIcon = isMMRPositive
    ? 'ph:trend-up'
    : isMMRNegative
    ? 'ph:trend-down'
    : 'ph:equals';

  const mmrText = isMMRPositive ? `+${mmrRounded} MMR` : `${mmrRounded} MMR`;

  const boxColors = ['bg-[#FC480D]', 'bg-[#0BCF46]', 'bg-[#FF6600]', 'bg-[#1D4ED8]'];

  return (
    <div
      className={`relative w-[540px] h-[960px] overflow-hidden select-none ${
        transparent ? 'bg-transparent shadow-none' : 'bg-[#0E1726] shadow-[0_8px_30px_rgba(14,23,38,0.18)]'
      } ${className}`}
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* Background Image & Gradient (only if not transparent) */}
      {!transparent && (
        <>
          <img
            src={activeBgImage}
            alt="Foto pertandingan"
            className="absolute inset-0 w-full h-full object-cover object-[58%_34%]"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg,rgba(14,23,38,.25) 0%,rgba(14,23,38,.05) 34%,rgba(0,0,0,.55) 62%,rgba(0,0,0,.92) 100%)',
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(14, 23, 38, 0.35) 0%, rgba(14, 23, 38, 0.15) 32%, rgba(14, 23, 38, 0.55) 62%, rgba(14, 23, 38, 0.95) 100%)',
            }}
          />
        </>
      )}

      {/* Content Container */}
      <div className="absolute inset-0 flex flex-col justify-end p-[26px_24px_24px]">
        {/* Dark Glassmorphic Box */}
        <div
          className="rounded-[20px] p-[28px_24px_24px] flex flex-col gap-[22px]"
          style={{
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'linear-gradient(180deg, rgba(20,20,22,0.62) 0%, rgba(12,12,14,0.86) 100%)',
            backdropFilter: transparent ? 'none' : 'blur(12px)',
          }}
        >
          {/* Top Row: Avatar & Identity + Sport Badge & MMR */}
          <div className="flex items-center justify-between gap-[16px]">
            {/* Player Info */}
            <div className="flex items-center gap-[16px] min-w-0">
              <img
                src={avatarSrc}
                alt={playerName}
                className={`w-[72px] h-[72px] rounded-full object-cover shrink-0 border-2 border-white/60 ${
                  transparent ? '' : 'shadow-md'
                }`}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR;
                }}
              />
              <div className="flex flex-col min-w-0">
                <div className="text-[26px] font-extrabold text-white leading-[30px] tracking-[-0.02em] uppercase truncate">
                  {playerName}
                </div>
                <div
                  className="text-[15px] font-semibold text-[#FFA96F] leading-[20px] truncate"
                  style={transparent ? undefined : { textShadow: '0 1px 8px rgba(14,23,38,0.8)' }}
                >
                  {eventName}
                </div>
                <div
                  className="text-[13px] font-medium text-white/80 leading-[18px] truncate"
                  style={transparent ? undefined : { textShadow: '0 1px 8px rgba(14,23,38,0.8)' }}
                >
                  {dateLoc}
                </div>
              </div>
            </div>

            {/* Badges: Sport & MMR */}
            <div className="flex flex-col items-end gap-[8px] shrink-0">
              <div
                className={`inline-flex items-center gap-[7px] bg-[#0E1726]/60 border border-white/20 backdrop-blur-sm rounded-full px-[14px] py-[7px] ${
                  transparent ? '' : 'shadow-md'
                }`}
              >
                <Icon icon={isRacket ? 'ph:tennis-ball-fill' : 'ph:soccer-ball-fill'} className="text-white text-[15px]" />
                <span
                  className="text-[12px] font-semibold tracking-[0.08em] text-white uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {sportCategory}
                </span>
              </div>
              <div
                className={`inline-flex items-center gap-[6px] rounded-full px-[13px] py-[7px] ${
                  transparent ? '' : 'shadow-md'
                } ${mmrBg}`}
              >
                <Icon icon={mmrIcon} className="text-white text-[15px]" />
                <span className="text-[13px] font-bold text-white">{mmrText}</span>
              </div>
            </div>
          </div>

          {/* 4 Stat Boxes in 1 Row */}
          <div className="flex items-stretch gap-[8px]">
            {statItems.map((st: { key: string; label: string; icon: string; iconBg: string; iconColor: string; value: string | number }, idx: number) => (
              <div
                key={st.key}
                className={`flex-1 aspect-square bg-white rounded-[16px] p-[14px] flex flex-col justify-between min-w-0 ${
                  transparent ? '' : 'shadow-[0_8px_24px_rgba(0,0,0,0.22)]'
                }`}
              >
                <div
                  className={`w-[32px] h-[32px] rounded-[9px] ${
                    boxColors[idx % boxColors.length]
                  } flex items-center justify-center shrink-0`}
                >
                  <Icon icon={st.icon} className="text-white text-[17px]" />
                </div>
                <div className="flex flex-col min-w-0">
                  <div className="text-[26px] sm:text-[28px] font-extrabold text-[#0E1726] leading-[30px] tracking-[-0.03em] truncate">
                    {st.value}
                  </div>
                  <div
                    className="text-[9px] sm:text-[10px] font-semibold tracking-[0.05em] text-[#8C8C8C] leading-[14px] uppercase truncate"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {st.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Logo */}
        <div className="flex justify-center pt-[22px]">
          <img src={SPORTO_LOGO_URL} alt="Sporto" className="h-[26px] w-auto object-contain block" />
        </div>
      </div>
    </div>
  );
};

export default KartuPemainCard;
